import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Zap } from "lucide-react";
import type { MultiSelectNode as MultiSelectNodeType, ChecklistItem, PatientInput } from "@/lib/pharmacyFirst/types";

interface MultiSelectNodeProps {
  node: MultiSelectNodeType;
  isDarkMode?: boolean;
  onAnswer: (selectedIds: string[]) => void;
  onSelectionChange?: (selections: string[]) => void;
  /**
   * Previously given answer, or null when the node has not been answered yet.
   * "None of the above" is recorded as an empty array, so the two cases must
   * stay distinguishable for Back to restore the tick.
   */
  value?: string[] | null;
  stepNumber?: number;
  patient?: PatientInput;
}

function isItemVisible(item: ChecklistItem, ageYears: number | null | undefined): boolean {
  if (item.showIfAgeAtLeast !== undefined && (ageYears == null || ageYears < item.showIfAgeAtLeast)) return false;
  if (item.showIfAgeUnder !== undefined && (ageYears == null || ageYears >= item.showIfAgeUnder)) return false;
  return true;
}

export function MultiSelectNode({
  node,
  isDarkMode = false,
  onAnswer,
  onSelectionChange,
  value = null,
  stepNumber,
  patient,
}: MultiSelectNodeProps) {
  const NONE_OPTION_ID = "__none_of_the_above__";
  const [selected, setSelected] = useState<string[]>(() => {
    // An answer of "no items" is what the none option records, so re-tick it.
    if (Array.isArray(value) && value.length === 0 && node.noneOption) {
      return [NONE_OPTION_ID];
    }
    const previousAnswer = value ?? [];
    const comorbidities = patient?.comorbidityList;
    if (comorbidities && comorbidities.length > 0) {
      const allItems = node.groups?.length
        ? node.groups.flatMap((g) => g.items)
        : node.items || [];
      const autoSelected = allItems
        .filter((item) => item.autoSelectIfComorbidity !== undefined && comorbidities.includes(item.autoSelectIfComorbidity))
        .map((item) => item.id);
      return [...new Set([...previousAnswer, ...autoSelected])];
    }
    return previousAnswer;
  });
  const [needsZeroConfirm, setNeedsZeroConfirm] = useState(false);
  const [prefilledIds] = useState<Set<string>>(() => {
    const comorbidities = patient?.comorbidityList;
    if (comorbidities && comorbidities.length > 0) {
      const allItems = node.groups?.length
        ? node.groups.flatMap((g) => g.items)
        : node.items || [];
      return new Set(
        allItems
          .filter((item) => item.autoSelectIfComorbidity !== undefined && comorbidities.includes(item.autoSelectIfComorbidity))
          .map((item) => item.id)
      );
    }
    return new Set<string>();
  });

  const ageYears = patient?.ageYears ?? null;

  const isFlat = !node.groups || node.groups.length === 0;
  const flatItems = (node.items || []).filter((i) => isItemVisible(i, ageYears));
  const minRequired = (node as any).minRequired as number | undefined;
  const hasCustomZeroSelectionLabel = node.noneOptionLabel !== undefined;
  const hasNoneOption = node.noneOption !== undefined;
  const noneSelected = selected.includes(NONE_OPTION_ID);

  // Per-group counts with age-filtered items
  const visibleGroups = (node.groups || []).map((g) => ({
    ...g,
    visibleItems: g.items.filter((i) => isItemVisible(i, ageYears)),
  }));
  const groupCounts = visibleGroups.map((g) => ({
    id: g.id,
    label: g.label,
    isKey: !!g.isKeyDiagnostic,
    count: g.visibleItems.filter((i) => selected.includes(i.id)).length,
    total: g.visibleItems.length,
  }));
  const emptyGroups = groupCounts.filter((g) => g.count === 0);

  const handleContinue = () => {
    if (noneSelected) {
      onAnswer([]);
      return;
    }
    if (selected.length === 0 && !needsZeroConfirm) {
      setNeedsZeroConfirm(true);
      return;
    }
    onAnswer(selected);
  };

  const handleToggle = (itemId: string) => {
    if (itemId === NONE_OPTION_ID) {
      const next = selected.includes(NONE_OPTION_ID) ? [] : [NONE_OPTION_ID];
      setSelected(next);
      setNeedsZeroConfirm(false);
      onSelectionChange?.(next.includes(NONE_OPTION_ID) ? [] : next);
    } else {
      setSelected((prev) => {
        const withoutNone = prev.filter((id) => id !== NONE_OPTION_ID);
        const next = withoutNone.includes(itemId)
          ? withoutNone.filter((id) => id !== itemId)
          : [...withoutNone, itemId];
        onSelectionChange?.(next);
        return next;
      });
    }
  };

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  return (
    <div className={cardClass}>
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
        </div>
      )}

      {stepNumber === undefined && (
        <div className="mb-4">
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
        </div>
      )}

      {(node as any).hint && (
        <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          {(node as any).hint}
        </p>
      )}

      {isFlat ? (
        <div
          className={`border rounded-lg p-3 space-y-2 ${
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"
          }`}
        >
          {flatItems.map((item) => (
            <div key={item.id} className="flex items-center space-x-2">
              <Checkbox
                id={`${node.id}-${item.id}`}
                checked={selected.includes(item.id)}
                onCheckedChange={() => handleToggle(item.id)}
              />
              <label
                htmlFor={`${node.id}-${item.id}`}
                className={`text-sm cursor-pointer flex-1 flex items-center justify-between ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                <span>{item.text}</span>
                {prefilledIds.has(item.id) && (
                  <span className={`inline-flex items-center gap-1 ml-2 px-2 py-1 rounded text-xs font-medium ${
                    isDarkMode
                      ? "bg-blue-950 text-blue-200 border border-blue-700"
                      : "bg-blue-100 text-blue-800 border border-blue-300"
                  }`}>
                    <Zap className="h-3 w-3" />
                    Auto
                  </span>
                )}
              </label>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {visibleGroups.map((group, gi) => {
            if (group.visibleItems.length === 0) return null;
            const gc = groupCounts[gi];
            const hasSelection = gc.count > 0;
            return (
              <div key={group.id}>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {group.label}
                  </h4>
                  {group.isKeyDiagnostic && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        hasSelection
                          ? isDarkMode
                            ? "bg-emerald-900 text-emerald-300"
                            : "bg-emerald-100 text-emerald-700"
                          : isDarkMode
                            ? "bg-gray-700 text-gray-400"
                            : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {hasSelection ? "✓ at least one selected" : "select at least one"}
                    </span>
                  )}
                </div>
                <div
                  className={`border rounded-lg p-3 space-y-2 ${
                    group.isKeyDiagnostic && !hasSelection
                      ? isDarkMode
                        ? "border-amber-700 bg-gray-700"
                        : "border-amber-300 bg-amber-50"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-700"
                        : "border-gray-200 bg-gray-50"
                  }`}
                >
                  {group.visibleItems.map((item) => (
                    <div key={item.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${node.id}-${item.id}`}
                        checked={selected.includes(item.id)}
                        onCheckedChange={() => handleToggle(item.id)}
                      />
                      <label
                        htmlFor={`${node.id}-${item.id}`}
                        className={`text-sm cursor-pointer flex-1 flex items-center justify-between ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                      >
                        <span>{item.text}</span>
                        {prefilledIds.has(item.id) && (
                          <span className={`inline-flex items-center gap-1 ml-2 px-2 py-1 rounded text-xs font-medium ${
                            isDarkMode
                              ? "bg-blue-950 text-blue-200 border border-blue-700"
                              : "bg-blue-100 text-blue-800 border border-blue-300"
                          }`}>
                            <Zap className="h-3 w-3" />
                            Auto
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasNoneOption && (
        <div className={`mt-3 pt-3 border-t ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}>
          <div
            className={`border rounded-lg p-3 ${
              isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${node.id}-${NONE_OPTION_ID}`}
                checked={noneSelected}
                onCheckedChange={() => handleToggle(NONE_OPTION_ID)}
              />
              <label
                htmlFor={`${node.id}-${NONE_OPTION_ID}`}
                className={`text-sm cursor-pointer font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                {node.noneOption!.label}
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t">
        {needsZeroConfirm && selected.length === 0 && !noneSelected && (
          <div
            className={`mb-3 p-3 rounded-md text-sm border ${
              isDarkMode
                ? "border-amber-700 bg-amber-900/20 text-amber-200"
                : "border-amber-300 bg-amber-50 text-amber-900"
            }`}
          >
            <p className="font-semibold mb-1">
              {hasCustomZeroSelectionLabel ? "Confirm selection" : "Nothing selected"}
            </p>
            {hasCustomZeroSelectionLabel ? (
              <p>{node.noneOptionLabel}</p>
            ) : (
              <p>
                No options selected
                {emptyGroups.length > 0 && emptyGroups.length < groupCounts.length
                  ? ` in ${emptyGroups.map((g) => `"${g.label}"`).join(" or ")}`
                  : ""}
                . Tap Continue again to confirm.
              </p>
            )}
          </div>
        )}

        <FlowPrimaryButton
          wrapperClassName="flex justify-end"
          onClick={handleContinue}
          variant={needsZeroConfirm && selected.length === 0 && !noneSelected ? "destructive" : "default"}
          label={
            needsZeroConfirm && selected.length === 0 && !noneSelected
              ? hasCustomZeroSelectionLabel
                ? "Confirm selection"
                : "Confirm"
              : "Continue"
          }
        />
      </div>
    </div>
  );
}
